import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import SnagLinkPicker, {
  SnagOption,
  snagOptionFromNode,
} from '@/components/SnagLinkPicker'
import { LoaderCircle, Link as LinkIcon } from 'lucide-react'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import MeetingFileUpload from './MeetingFileUpload'
import { toDateInputValue } from '../../../utils/formatters'

interface Props {
  isOpen: boolean
  onClose: (saved?: boolean) => void
  actionItemId: string | null
  meetingId: string | null
}

const DEFAULT = {
  title: '',
  description: '',
  assignedToId: '',
  dueDate: '',
  status: 'OPEN' as string,
  priority: 'MEDIUM' as string,
  linkedSnagId: '',
  linkedDocId: '',
}

const ActionItemModal: React.FC<Props> = ({ isOpen, onClose, actionItemId, meetingId }) => {
  const { t } = useTranslation()
  const [form, setForm] = useState(DEFAULT)
  const [submitting, setSubmitting] = useState(false)

  // Load action item for editing (query single action item via the meeting list)
  const { data: meData } = useQuery(Query.ME)
  const projectId = meData?.me?.currentProject?.id
  const { data: aiData } = useQuery(Query.PROJECT_ACTION_ITEMS, {
    variables: { projectId },
    skip: !projectId || !actionItemId,
  })

  const { data: projectData } = useQuery(Query.PROJECT, {
    variables: { id: projectId },
    skip: !projectId,
  })
  const users = (projectData?.project?.users || []).map((pu: any) => ({
    id: pu.user.id,
    name: pu.user.name,
  }))

  const [selectedSnagOption, setSelectedSnagOption] = useState<SnagOption | null>(
    null
  )

  useEffect(() => {
    if (!isOpen) return
    if (actionItemId && aiData?.projectActionItems) {
      const a = aiData.projectActionItems.find((x: any) => x.id === actionItemId)
      if (a) {
        setForm({
          title: a.title || '',
          description: a.description || '',
          assignedToId: a.assignedTo?.id || '',
          dueDate: toDateInputValue(a.dueDate),
          status: a.status,
          priority: a.priority || 'MEDIUM',
          linkedSnagId: a.linkedSnag?.id || '',
          linkedDocId: a.linkedDoc?.id || '',
        })
        setSelectedSnagOption(a.linkedSnag ? snagOptionFromNode(a.linkedSnag) : null)
      }
    } else if (!actionItemId) {
      setForm(DEFAULT)
      setSelectedSnagOption(null)
    }
  }, [isOpen, actionItemId, aiData])

  const [createActionItem] = useMutation(Mutation.CREATE_ACTION_ITEM)
  const [updateActionItem] = useMutation(Mutation.UPDATE_ACTION_ITEM)
  const [setActionItemFiles] = useMutation(Mutation.SET_ACTION_ITEM_FILES)
  const actionItem = actionItemId
    ? aiData?.projectActionItems?.find((x: any) => x.id === actionItemId)
    : null

  const canSave = !!form.title

  const handleSave = async () => {
    setSubmitting(true)
    try {
      if (actionItemId) {
        await updateActionItem({
          variables: {
            id: actionItemId,
            input: {
              title: form.title,
              description: form.description || null,
              status: form.status,
              priority: form.priority,
              assignedToId: form.assignedToId || null,
              dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
              linkedSnagId: form.linkedSnagId || null,
              linkedDocId: form.linkedDocId || null,
            },
          },
        })
      } else if (meetingId) {
        await createActionItem({
          variables: {
            input: {
              meetingId,
              title: form.title,
              description: form.description || null,
              priority: form.priority,
              assignedToId: form.assignedToId || null,
              dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
              linkedSnagId: form.linkedSnagId || null,
              linkedDocId: form.linkedDocId || null,
            },
          },
        })
      }
      toastr.success(t('Common_success'), t('ActionItem_saved'))
      onClose(true)
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message || 'Unknown error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {actionItemId ? t('ActionItem_edit_title') : t('ActionItem_new_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>{t('ActionItem_field_title')}</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>

          {projectId && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <LinkIcon className="h-3.5 w-3.5" />
                {t('ActionItem_field_linked_snag')}
              </Label>
              <SnagLinkPicker
                projectId={projectId}
                value={selectedSnagOption}
                onChange={(opt) => {
                  setSelectedSnagOption(opt)
                  setForm({ ...form, linkedSnagId: opt ? opt.value : '' })
                }}
                placeholder={t('ActionItem_linked_snag_placeholder')}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('ActionItem_field_description')}</Label>
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('ActionItem_field_assignee')}</Label>
              <Select
                value={form.assignedToId || ''}
                onValueChange={(v) => setForm({ ...form, assignedToId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u: any) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('ActionItem_field_due_date')}</Label>
              <Input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              />
            </div>
          </div>

          <div className={actionItemId ? 'grid grid-cols-2 gap-4' : ''}>
            <div className="space-y-2">
              <Label>{t('ActionItem_field_priority')}</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm({ ...form, priority: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRITICAL">{t('ActionItem_priority_critical')}</SelectItem>
                  <SelectItem value="HIGH">{t('ActionItem_priority_high')}</SelectItem>
                  <SelectItem value="MEDIUM">{t('ActionItem_priority_medium')}</SelectItem>
                  <SelectItem value="LOW">{t('ActionItem_priority_low')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actionItemId && (
              <div className="space-y-2">
                <Label>{t('ActionItem_field_status')}</Label>
                <Select
                  value={form.status}
                  onValueChange={(v) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">{t('ActionItem_status_open')}</SelectItem>
                    <SelectItem value="IN_PROGRESS">{t('ActionItem_status_in_progress')}</SelectItem>
                    <SelectItem value="RESOLVED">{t('ActionItem_status_resolved')}</SelectItem>
                    <SelectItem value="CLOSED">{t('ActionItem_status_closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {meetingId || actionItem ? (
            <div className="space-y-2">
              <Label>{t('Photos_task_photos')}</Label>
              {actionItemId ? (
                actionItem && (
                  <MeetingFileUpload
                    meetingId={meetingId || actionItem.meeting?.id}
                    projectId={projectId}
                    allowUpload={false}
                    files={actionItem.files || []}
                    onFilesChange={(fileIds) =>
                      setActionItemFiles({ variables: { id: actionItemId, fileIds } })
                    }
                    compact
                  />
                )
              ) : (
                <p className="text-xs text-muted-foreground">{t('Photos_task_photos_after_save')}</p>
              )}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onClose()} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave || submitting}>
            {submitting && <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />}
            {t('Common_save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ActionItemModal
